import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Box, AlertCircle } from "lucide-react";
import { useAddToPool, type MinifigCondition, MINIFIG_CONDITION_SHORT_LABEL } from "@/hooks/useUserSets";
import { toast } from "sonner";

interface AddSetQuickModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddSetQuickModal({ open, onOpenChange, onSuccess }: AddSetQuickModalProps) {
  const addToPool = useAddToPool();
  const [setRef, setSetRef] = useState("");
  const [minifigCondition, setMinifigCondition] = useState<MinifigCondition>("complete");
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState<null | "empty" | "not_found">(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanRef = setRef.trim();
    if (!cleanRef) {
      setHint("empty");
      return;
    }
    setHint(null);
    setLoading(true);

    try {
      const result = await addToPool(cleanRef, minifigCondition);
      if (result === null) {
        setHint("not_found");
        return;
      }
      toast.success(`Set #${cleanRef} añadido con éxito a tu pool de Brickshare.`);
      setSetRef("");
      setMinifigCondition("complete");
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      toast.error(err.message || "Error al añadir el set.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl p-6">
        <DialogHeader className="text-left space-y-2">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-1">
            <Box className="h-6 w-6" />
          </div>
          <DialogTitle className="text-xl font-display font-bold">Añadir set a la comunidad</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Introduce el número de referencia oficial de LEGO® (ej. 75192, 10300, 21330) para catalogarlo e incorporarlo a tu pool de préstamos.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="quick-set-ref" className="text-xs font-semibold">
              Número de set LEGO®
            </Label>
            <Input
              id="quick-set-ref"
              placeholder="Ej: 10294 o 75313"
              value={setRef}
              onChange={(e) => {
                setSetRef(e.target.value);
                if (hint) setHint(null);
              }}
              className="rounded-xl h-11 text-base font-mono"
              autoFocus
            />
            {hint === "empty" && (
              <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                <AlertCircle className="h-3.5 w-3.5" />
                Por favor, escribe el número de referencia del set.
              </p>
            )}
            {hint === "not_found" && (
              <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                <AlertCircle className="h-3.5 w-3.5" />
                No se encontró ese set en el catálogo oficial de LEGO®. Revisa el número.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="quick-minifig-condition" className="text-xs font-semibold">
              Estado de las minifiguras y accesorios
            </Label>
            <Select
              value={minifigCondition}
              onValueChange={(val) => setMinifigCondition(val as MinifigCondition)}
            >
              <SelectTrigger id="quick-minifig-condition" className="rounded-xl h-11">
                <SelectValue placeholder="Selecciona estado" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="complete">
                  {MINIFIG_CONDITION_SHORT_LABEL.complete} (100% figuras originales)
                </SelectItem>
                <SelectItem value="alternative_minifigs">
                  {MINIFIG_CONDITION_SHORT_LABEL.alternative_minifigs}
                </SelectItem>
                <SelectItem value="no_minifigs">
                  {MINIFIG_CONDITION_SHORT_LABEL.no_minifigs}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="pt-2 flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-xl h-11 w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-xl h-11 gradient-hero font-semibold w-full sm:w-auto gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Buscando en LEGO®...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Publicar en mi pool
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
export default AddSetQuickModal;
